import { useEffect, useState, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/i18n/LocaleContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { openCalendly } from "@/lib/calendly";

interface CountdownItem {
  value: number;
  label: string;
}

const CountdownTimer = ({
  timeLeft,
  t,
  className = "",
}: {
  timeLeft: { days: number; hours: number; minutes: number; seconds: number };
  t: (key: string) => string;
  className?: string;
}) => {
  const prefersReducedMotion = useReducedMotion();
  const noMotion = prefersReducedMotion;
  const countdownVariants = noMotion
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 } }
    : { hidden: { opacity: 0, scale: 0.8 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" as const } } };

  const countdownItems: CountdownItem[] = [
    { value: timeLeft.days, label: t("hero.countdown.days") },
    { value: timeLeft.hours, label: t("hero.countdown.hours") },
    { value: timeLeft.minutes, label: t("hero.countdown.min") },
    { value: timeLeft.seconds, label: t("hero.countdown.sec") },
  ];

  return (
    <motion.div
      className={`bg-primary-foreground/10 backdrop-blur-sm rounded-lg p-3 w-fit ${className}`}
      variants={countdownVariants}
      initial="hidden"
      animate="visible"
      role="timer"
      aria-live="polite"
      aria-atomic="true"
      aria-label={t("hero.countdown.label")}
    >
      <p className="text-primary-foreground/90 text-xs mb-1" aria-hidden="true">{t("hero.countdown.label")}</p>
      <div className="flex gap-3" aria-hidden="true">
        {countdownItems.map((item, index) => (
          <motion.div
            key={index}
            className="text-center"
            initial={noMotion ? { opacity: 1 } : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={noMotion ? { duration: 0 } : { delay: 0.8 + index * 0.1 }}
          >
            <span className="block text-xl md:text-2xl font-bold text-accent">
              {item.value.toString().padStart(2, "0")}
            </span>
            <span className="text-[10px] text-primary-foreground/80">{item.label}</span>
          </motion.div>
        ))}
      </div>
      <span className="sr-only">
        {countdownItems.map((i) => `${i.value} ${i.label}`).join(", ")}
      </span>
    </motion.div>
  );
};

const HeroSection = () => {
  const { t, locale } = useLocale();
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const prevTimeRef = useRef("");

  useEffect(() => {
    const targetDate = new Date("2026-06-30T23:59:59");
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(difference / (1000 * 60 * 60) % 24),
          minutes: Math.floor(difference / (1000 * 60) % 60),
          seconds: Math.floor(difference / 1000 % 60),
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);


  const noMotion = prefersReducedMotion;
  const containerVariants = noMotion
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 } }
    : { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.3 } } };
  const itemVariants = noMotion
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 } }
    : { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } } };

  const showCountdown = !(isMobile && locale === "en");

  return (
    <section className="relative min-h-[600px] lg:min-h-[700px] flex items-center overflow-hidden">
      {/* LCP image — rendered as <img> with fetchpriority="high" for fastest load */}
      <img
        src="/hero-immigrants.webp"
        alt={t("hero.imageAlt")}
        fetchPriority="high"
        loading="eager"
        decoding="sync"
        width={1024}
        height={559}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/70 to-primary/50" />

      <div className="relative z-10 container mx-auto px-4 py-12 lg:py-24">
        <motion.div className="max-w-3xl" variants={containerVariants} initial="hidden" animate="visible">
          <motion.h1
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6"
            variants={itemVariants}
          >
            {t("hero.title")}
          </motion.h1>
          <motion.p className="text-lg md:text-xl text-primary-foreground/90 mb-8" variants={itemVariants}>
            {locale === "es"
              ? t("hero.subtitle")
              : (
                <>
                  {t("hero.subtitle")}
                  <span className="font-bold text-accent">{t("hero.subtitleHighlight")}</span>
                  {t("hero.subtitleAfter")}
                </>
              )}
          </motion.p>
          <motion.div variants={itemVariants} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              onClick={() => openCalendly(locale)}
              size="lg"
              className="w-full md:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold text-lg px-8 py-6"
            >
              {t("hero.cta")}
            </Button>
          </motion.div>
          {showCountdown && (
            <motion.div variants={itemVariants} className="mt-6 lg:hidden">
              <CountdownTimer timeLeft={timeLeft} t={t} />
            </motion.div>
          )}
        </motion.div>
      </div>

      {showCountdown && (
        <div className="hidden lg:block absolute bottom-6 right-6 z-10">
          <CountdownTimer timeLeft={timeLeft} t={t} />
        </div>
      )}
    </section>
  );
};

export default HeroSection;
